import { type Editor } from '@tiptap/core';
import { useEffect, useState } from 'react';

import { type Skill } from '../../skills';

export type SkillHoverState = {
  commandEl: HTMLElement;
  getRect: () => DOMRect;
  skill: Skill;
};

function isSkillCommandElement(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) {
    return null;
  }

  const commandEl = node.closest('[data-skill-command]');

  return commandEl instanceof HTMLElement ? commandEl : null;
}

export const useSkillHover = (editor: Editor | null, skillsRef: { current: Skill[] }, enabled: boolean) => {
  const [skillHover, setSkillHover] = useState<SkillHoverState | null>(null);

  useEffect(() => {
    if (!editor || !enabled) {
      setSkillHover(null);
      return;
    }

    const dom = editor.view.dom;

    const showHoverForCommand = (commandEl: HTMLElement) => {
      const slashCommand = commandEl.getAttribute('data-skill-command');
      const skill = slashCommand ? skillsRef.current.find((entry) => entry.slashCommand === slashCommand) : undefined;

      if (!skill) {
        setSkillHover(null);
        return;
      }

      setSkillHover({
        commandEl,
        getRect: () => commandEl.getBoundingClientRect(),
        skill,
      });
    };

    const handleMouseOver = (event: MouseEvent) => {
      const commandEl = isSkillCommandElement(event.target);

      if (!commandEl) {
        setSkillHover(null);
        return;
      }

      const from = event.relatedTarget;

      if (from instanceof Node && commandEl.contains(from)) {
        return;
      }

      showHoverForCommand(commandEl);
    };

    const handleMouseOut = (event: MouseEvent) => {
      const related = event.relatedTarget;

      if (related instanceof Element && related.closest('[data-skill-command]')) {
        return;
      }

      setSkillHover(null);
    };

    const handleFocusIn = (event: FocusEvent) => {
      const commandEl = isSkillCommandElement(event.target);

      if (!commandEl) {
        return;
      }

      showHoverForCommand(commandEl);
    };

    const handleFocusOut = (event: FocusEvent) => {
      const related = event.relatedTarget;

      if (related instanceof Element && related.closest('[data-skill-command]')) {
        return;
      }

      setSkillHover(null);
    };

    dom.addEventListener('mouseover', handleMouseOver);
    dom.addEventListener('mouseout', handleMouseOut);
    dom.addEventListener('focusin', handleFocusIn);
    dom.addEventListener('focusout', handleFocusOut);

    return () => {
      dom.removeEventListener('mouseover', handleMouseOver);
      dom.removeEventListener('mouseout', handleMouseOut);
      dom.removeEventListener('focusin', handleFocusIn);
      dom.removeEventListener('focusout', handleFocusOut);
      setSkillHover(null);
    };
  }, [editor, enabled, skillsRef]);

  return { skillHover };
};
